import { useState, useEffect, useRef } from "react";
import { I18n, Storage } from 'aws-amplify';
import { useSetRecoilState } from "recoil";
import { closePanelTabSelector } from '@/state/selector/panelSelector';
import './AiAutoLog.css';

import Constant from "@/common/constant/Constant";
import AiAutoConstant from "@/common/constant/AiAutoConstant";
import Utils from '@/common/utils/utils';

import {showLoadingMouse, hideLoadingMouse} from '@/common/utils/CustomCursor';
import { getTicketById } from '@/service/ticketService';
import { getAiAutoLog } from "../../ticketKnowledge/handler/DetailTicketHandler";
import { updateAiAutoLog } from '@/service/aiAutoLogService';

import CircularProgress from '@material-ui/core/CircularProgress';
import ConfirmDialog from '@/common/dialog/ConfirmDialog';
import { SnackbarProvider, useSnackbar } from 'notistack';
import { Button } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { PropTypes } from 'prop-types';

SDautoLog.propTypes = {
    corporationDetail: PropTypes.any,
    selectedProject: PropTypes.any,
    currentUserDetail: PropTypes.any,
    ticketDetail: PropTypes.any,
    panelId: PropTypes.any,
    isDisabled: PropTypes.any,
};

function SDautoLog({
    corporationDetail,
    selectedProject,
    currentUserDetail,
    ticketDetail,
    panelId,
    isDisabled
}) {
    const [isExpand, setIsExpand] = useState(false)
    const [listSDautoLog, setListSDautoLog] = useState([]);
    const [isShowSeeMore, setIsShowSeeMore] = useState(false);
    const [isAutoDeleteLogSetting, setIsAutoDeleteLogSetting] = useState(false);
    const [isLoadingGetLog, setIsLoadingGetLog] = useState(false);
    const [errorMessagePopup, setErrorMessagePopup] = useState({ isShow: false});
    const [pausingLogList, setPausingLogList] = useState([]);
    const retryQueryLogsInterval = useRef(null);
    const toastRef = useRef(null);
    const { enqueueSnackbar, closeSnackbar } = useSnackbar();

    const closePanelTab = useSetRecoilState(closePanelTabSelector);

    useEffect(() => {
        let promptData = Utils.convertJSONDataToObject(ticketDetail.promptData);
        let autoDeleteLog = true;
        if (!Utils.isEmptyObject(promptData)) {
            if (typeof promptData.autoDeleteLog === "boolean") {
                autoDeleteLog = promptData.autoDeleteLog;
            }
        }
        setIsAutoDeleteLogSetting(autoDeleteLog);
    }, []);

    // handle tracking pausing log list to update log status
    useEffect(() => {
        if (Utils.isEmptyArray(pausingLogList)) return;

        if (retryQueryLogsInterval.current) {
            clearInterval(retryQueryLogsInterval.current);
        }
        retryQueryLogsInterval.current = setInterval(async() => {
            // Placeholder for retry logic
            console.log('Retry query SDauto logs');
        }, 30000); // 30s

        return () => clearInterval(retryQueryLogsInterval.current);
    }, [pausingLogList]);
    
    const handleReloadLogList = async () => {
        setIsLoadingGetLog(true);
        if (retryQueryLogsInterval) {
            clearInterval(retryQueryLogsInterval.current);
        }

        const args = {
            corporationDetail,
            selectedProject,
            currentUserDetail,
            limit: listSDautoLog.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT ? AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT : listSDautoLog.length
        }
        const {listLog: reloadList, error} = await getAiAutoLog(args, ticketDetail.id);
        if (!Utils.isEmptyString(error)) {
            setErrorMessagePopup({isShow: true, msgCode: error})
            return;
        }

        setListSDautoLog(reloadList);
        setIsShowSeeMore((!reloadList.length || reloadList.length < listSDautoLog.length || reloadList.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT) ? false : true);

        setIsLoadingGetLog(false);
    }
    
    const handleSeeMore = async () => {
        try {
            showLoadingMouse();

            const currentLastLog = listSDautoLog[listSDautoLog.length - 1];

            const args = {
                corporationDetail,
                selectedProject,
                currentUserDetail,
                lastEvaluatedKey: {
                    id: { S: currentLastLog.id },
                    createdAt: { S: currentLastLog.createdAt },
                    ticketId: { S: currentLastLog.ticketId }
                },
                limit: AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
            }
            const {listLog: getMoreList, error} = await getAiAutoLog(args, ticketDetail.id);
            if (!Utils.isEmptyString(error)) {
                setErrorMessagePopup({isShow: true, msgCode: error})
                return;
            }
            setListSDautoLog(prev => [...prev, ...getMoreList]);
            setIsShowSeeMore((!getMoreList.length || getMoreList.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT) ? false : true);

            hideLoadingMouse();
        } catch (error) {
            hideLoadingMouse();
        }
    }

    const handleOpenTabLog = async (logDetail) => {
        if (!logDetail) return;
        const existTicket = await getTicketById(null, ticketDetail.id);
        if (!existTicket) {
            setErrorMessagePopup({isShow: true, msgCode: Constant.DATA_CHANGED_PLEASE_RELOAD_ERROR})
            return;
        }

        try {
            showLoadingMouse();
            
            // Tạo S3 key cho SDauto log
            const s3Key = `sdauto/logs/${logDetail.chatThreadId}/log_entries.json`;
            
            // Lấy URL download từ S3
            const logUrl = await Storage.get(s3Key, { expires: 7200 });
            
            // Mở link trong tab mới
            window.open(logUrl, '_blank', 'noopener,noreferrer');
            
            hideLoadingMouse();
        } catch (error) {
            console.error("Error opening SDauto log:", error);
            hideLoadingMouse();
            enqueueSnackbar(I18n.get('MSG-SD-AUTO-LOG-OPEN-FAILED'), {
                variant: 'error',
                autoHideDuration: 3000
            });
        }
    }

    const handleUpdateLogByAction = async (actionType, logDetail) => {
        if (isDisabled) return;
        try {
            showLoadingMouse();

            let listSDautoLogClone = [...listSDautoLog];
            let requestDto = {
                ticketId: ticketDetail.id,
                aiChatThreadId: logDetail.id,
                actionType: actionType,
                userId: currentUserDetail.id,
                corporationInuse: corporationDetail.id,
                projectInuse: selectedProject.id,
            }
            
            let response = await updateAiAutoLog(requestDto);
            
            let logDetailUpdated = Utils.convertJSONDataToObject(response.data);

            if (!logDetailUpdated || Utils.isEmptyObject(logDetailUpdated)) {
                hideLoadingMouse();
                setErrorMessagePopup({ isShow: true, msgCode: response.errorCode })
                return;
            }

            switch (actionType) {
                case AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_LOG:
                    listSDautoLogClone = listSDautoLogClone.filter(log => log.id !== logDetail.id);
                    
                    // handle get more 1 log to replace deleted log if isShowSeeMore == true
                    if (isShowSeeMore) {
                        const currentLastLog = listSDautoLogClone[listSDautoLogClone.length - 1];
                        const args = {
                            corporationDetail,
                            selectedProject,
                            currentUserDetail,
                            lastEvaluatedKey: {
                                id: { S: currentLastLog.id },
                                createdAt: { S: currentLastLog.createdAt },
                                ticketId: { S: currentLastLog.ticketId }
                            },
                            limit: 1
                        }
                        const {listLog: getMoreList} = await getAiAutoLog(args, ticketDetail.id);
                        if (!Utils.isEmptyArray(getMoreList)) {
                            listSDautoLogClone = [...listSDautoLogClone, ...getMoreList]
                        }
                    }

                    // close tabChatAI
                    closePanelTab({
                        event: null,
                        panelType: Constant.PANEL_TYPE.OPEN_CHAT_AI,
                        panelId: panelId,
                        tabId: logDetail.chatThreadId
                    });
                    break;
                default:
                    break;
            }
    
            setListSDautoLog(listSDautoLogClone);
            if (Utils.isEmptyArray(listSDautoLogClone) || listSDautoLogClone.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT) {
                setIsShowSeeMore(false);
            }
    
            hideLoadingMouse();
        } catch (error) {
            console.log("handleUpdateLogByAction error: ", error);
            hideLoadingMouse();
        }
    }

    const handleToggleSDautoDeleteLog = async () => {
        if (isDisabled) return;
        try {
            setIsAutoDeleteLogSetting(!isAutoDeleteLogSetting)
            let requestDto = {
                ticketId: ticketDetail.id,
                aiChatThreadId: 'fake',
                actionType: AiAutoConstant.THREAD_LOG_UPDATE_API.UPDATE_AUTO_DELETE_LOG_FLAG,
                userId: currentUserDetail.id,
                corporationInuse: corporationDetail.id,
                projectInuse: selectedProject.id,
            }

            let response = await updateAiAutoLog(requestDto);
            if (response.status === false) {
                hideLoadingMouse();
                setErrorMessagePopup({
                    isShow: true,
                    msgCode: response.errorCode
                })
                setIsAutoDeleteLogSetting(!isAutoDeleteLogSetting)
                return;
            }
        } catch (error) {
            console.log("handleToggleSDautoDeleteLog error: ", error);
            hideLoadingMouse();
        }
    }

    const renderLogStatus = (status) => {
        if (!status) return <></>;
        switch (status) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                return <></>;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                return <img src='/assets/image/status-stop.svg' alt='status-stop' />
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
                return <img src='/assets/image/status-success.svg' alt='status-success' />
            case Constant.CHATTHREAD_STATUS.ERROR:
                return <img src='/assets/image/status-error.svg' alt='status-error' />
            default:
                return <></>;
        }
    }

    const renderLogTimer = (log) => {
        if (!log) return '';
        const startTime = log.startTime ? Utils.getJSTDateTimeYYYY_MM_DDHHMM(log.startTime) : '';
        const endTime = log.endTime ? Utils.getJSTDateTimeYYYY_MM_DDHHMM(log.endTime) : '';
        const updatedTime = Utils.getJSTDateTimeYYYY_MM_DDHHMM(log.updatedAt);
        
        let endStatus = '';
        const logStatus = log.status
        switch (logStatus) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSING:
                endStatus = 'RUNNING...';
                break;
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.PAUSED:
                endStatus = `STOP ${updatedTime}`;
                break;
            default:
                endStatus = `END ${endTime}`;
                break;
        }
      
        return `START ${startTime}  ${endStatus}`;
    }

    const renderLogActionIcon = (log) => {
        let className = 'ai-log-btn';
        if (isDisabled) className += ' disabled';
        switch (log.status) {
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.RUNNING:
                return (
                    <span onClick={async () => await handleUpdateLogByAction(AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_LOG, log)} className={className}>
                        <img src='/assets/image/icon-trash.svg' alt='icon-trash' />
                    </span>
                )
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.DONE:
            case AiAutoConstant.AICHATTHREAD_LOG_STATUS.ERROR:
                return (
                        <span onClick={async () => await handleUpdateLogByAction(AiAutoConstant.THREAD_LOG_UPDATE_API.DELETE_LOG, log)} className={className}>
                        <img src='/assets/image/icon-trash.svg' alt='icon-trash' />
                    </span>
                );
            default:
                return <></>
        }
    }

    const handleExpandSDautoLog = async () => {
        if (!isExpand && Utils.isEmptyArray(listSDautoLog)) {
            setIsLoadingGetLog(true);
            showLoadingMouse();
            try {
                const args = {
                    corporationDetail,
                    selectedProject,
                    currentUserDetail,
                    limit: AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT
                }
                // get init log list
                const {listLog: initList, error} = await getAiAutoLog(args, ticketDetail.id);
                if (!Utils.isEmptyString(error)) {
                    setErrorMessagePopup({isShow: true, msgCode: error})
                    setIsLoadingGetLog(false);
                    hideLoadingMouse();
                    return;
                }
                setListSDautoLog(initList);
                setIsShowSeeMore(initList.length < AiAutoConstant.AI_AUTO_LOG_QUERY_LIMIT ? false : true);
            } catch (error) {
                console.error("Error fetching SDauto logs:", error);
            }
            setIsLoadingGetLog(false);
            hideLoadingMouse();
        }

        setIsExpand(prev => !prev);
    }

    return (
        <SnackbarProvider
            maxSnack={10}
            ref={toastRef}
            anchorOrigin={{vertical: 'top', horizontal: 'right'}}
            action={key => (
                <Button className="notistack-button" onClick={() => closeSnackbar(key)}>
                    <CloseIcon />
                </Button>
            )}
        >
            <div
                className="accordionHead-SettingTitle"
                onClick={async() => await handleExpandSDautoLog()}
            >
                <img src="/assets/image/execute-log.svg" alt="execute-log.svg" />
                <span className="text">{I18n.get('EXECUTION_LOG')}</span>
                {isExpand ? (
                    <img src="/static/media/icon-minus-accordion.24e40269.svg" alt="minus" />
                ) : (
                    <img src="/static/media/icon-plus-accordion.d9a73563.svg" alt="plus" />
                )}
            </div>
            {isLoadingGetLog ? (<div className="ai-auto-log-inner ai-log-flex justify-center"><CircularProgress color="primary" size="1.4rem"/></div>) : isExpand ? (
                <div className="ai-auto-log-inner">
                    <div className="ai-auto-log-sub-header ai-log-flex justify-between">
                        <div className="ai-log-flex justify-between gap-4">
                            <span className="ai-log-text f-weight-700">{I18n.get('EXECUTION_LOG')}</span>
                            <span onClick={async () => await handleReloadLogList()} className="ai-log-btn"><img src="/assets/image/reload-icon.svg" alt="reload-icon" /></span>
                        </div>
                        <div className="ai-log-flex justify-between gap-4">
                            <span className="ai-log-text f-weight-500">{I18n.get('AI_AUTO_DELETE_LOG_SUBSCRIPTION')}</span>
                            <div className="switch-check">
                                <input
                                    name="sdauto-delete-log-toggle"
                                    id="sdauto-delete-log-toggle"
                                    type="checkbox"
                                    checked={isAutoDeleteLogSetting}
                                    disabled={isDisabled}
                                    onChange={e => {handleToggleSDautoDeleteLog()}}
                                />
                                <label htmlFor="sdauto-delete-log-toggle"></label>
                            </div>
                        </div>
                    </div>
                    <div className="ai-log-flex vertical gap-8 ai-log-list ">
                        {listSDautoLog.length > 0 ? listSDautoLog.map(log => {
                            return (
                                <div className="log-ai-detail-wrapper ai-log-flex" key={log.id}>
                                    <div className="log-ai-status">
                                        {renderLogStatus(log?.status)}
                                    </div>
                                    <span className="log-ai-runtime ai-log-text" onClick={async() => await handleOpenTabLog(log)}>
                                        {renderLogTimer(log)}
                                    </span>
                                    <div className="log-ai-action ai-log-flex justify-end gap-8">
                                        {renderLogActionIcon(log)}
                                    </div>
                                </div>
                            )
                        }) : (
                            <p style={{margin: '0 auto'}}>{I18n.get('EMPTY_EXECUTION_LOG')}</p>
                        )}
                    </div>
                    <div className="ai-log-footer ai-log-flex justify-end">
                        {isShowSeeMore && <span onClick={async() => await handleSeeMore()} className="ai-log-see-more">{I18n.get('COMBTN009')}</span>}
                    </div>
                </div>
            ) : (<></>)}
            
            <ConfirmDialog
                isOpen={errorMessagePopup.isShow}
                msgConfirm={I18n.get(errorMessagePopup?.msgCode || 'MSG-SD-AUTO-LOG-CHANGED-DATA')}
                textSubmit={'OK'}
                okCallBack={() => {
                    setErrorMessagePopup({...errorMessagePopup, isShow: false})
                    window.location.reload();
                }}
            />
        </SnackbarProvider>
    );
}

export default SDautoLog; 